import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useDraft } from '../useDraft'
import DraftPrompt from '../components/DraftPrompt'
import { useLanguage } from '../LanguageContext'
import { uploadPassportDocument } from '../api'

const AIRPORTS_URL = 'https://raw.githubusercontent.com/mwgg/Airports/master/airports.json'

let airportCache = null

function useAirports() {
  const [airports, setAirports] = useState(airportCache || [])
  const [loading,  setLoading]  = useState(!airportCache)

  useEffect(() => {
    if (airportCache) { setLoading(false); return }
    fetch(AIRPORTS_URL)
      .then(r => r.json())
      .then(data => {
        const list = Object.values(data)
          .filter(a => a.iata && a.iata.length === 3 && a.name && a.city)
          .map(a => ({ code: a.iata, name: a.name, city: a.city, country: a.country || '' }))
        airportCache = list
        setAirports(list)
        setLoading(false)
      })
      .catch(() => { airportCache = []; setLoading(false) })
  }, [])

  return { airports, loading }
}

function useAirportSearch(airports, query) {
  return useMemo(() => {
    if (!query || query.length < 2) return []
    const q = query.toLowerCase()
    return airports
      .filter(a =>
        a.code.toLowerCase().startsWith(q) ||
        a.city.toLowerCase().startsWith(q) ||
        a.name.toLowerCase().includes(q)   ||
        a.city.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        const aCode = a.code.toLowerCase().startsWith(q) ? 0 : 1
        const bCode = b.code.toLowerCase().startsWith(q) ? 0 : 1
        return aCode - bCode
      })
      .slice(0, 8)
  }, [airports, query])
}

function useIsMobile() {
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent))
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return mobile
}

// ── Date helpers ──────────────────────────────────────────────────────────────
function isoToDisplay(iso) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return ''
  return `${d}/${m}/${y}`
}

function parseIso(iso) {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  return { year: y, month: m - 1, day: d } // month is 0-indexed
}

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
]
const DAYS_SHORT = ['Mo','Tu','We','Th','Fr','Sa','Su']

// ── DatePicker ────────────────────────────────────────────────────────────────
function DatePicker({ id, value, onChange, minIso }) {
  const parsed   = parseIso(value)
  const today    = new Date()
  const minParsed = parseIso(minIso)

  const [open, setOpen]     = useState(false)
  const [viewYear,  setViewYear]  = useState(parsed?.year  ?? today.getFullYear())
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.getMonth())
  const wrapRef = useRef(null)

  useEffect(() => {
    if (parsed) { setViewYear(parsed.year); setViewMonth(parsed.month) }
  }, [value])

  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1) }
    else setViewMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1) }
    else setViewMonth(m => m + 1)
  }

  const selectDay = (day) => {
    const mm = String(viewMonth + 1).padStart(2, '0')
    const dd = String(day).padStart(2, '0')
    onChange(`${viewYear}-${mm}-${dd}`)
    setOpen(false)
  }

  const isSelected = (day) => {
    if (!parsed) return false
    return parsed.year === viewYear && parsed.month === viewMonth && parsed.day === day
  }

  const isDisabled = (day) => {
    if (!minParsed) return false
    if (viewYear < minParsed.year) return true
    if (viewYear === minParsed.year && viewMonth < minParsed.month) return true
    if (viewYear === minParsed.year && viewMonth === minParsed.month && day < minParsed.day) return true
    return false
  }

  const isToday = (day) => {
    return today.getFullYear() === viewYear &&
           today.getMonth()    === viewMonth &&
           today.getDate()     === day
  }

  const firstDow = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const startOffset = (firstDow + 6) % 7
  const cells = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)

  return (
    <div className="svc-datepicker" ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={`svc-input svc-datepicker__trigger${value ? '' : ' svc-datepicker__trigger--empty'}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span>{value ? isoToDisplay(value) : 'DD/MM/YYYY'}</span>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true" className="svc-datepicker__icon">
          <rect x="1" y="2.5" width="11" height="9.5" rx="1" stroke="currentColor" strokeWidth="0.85" fill="none"/>
          <path d="M4 1v3M9 1v3M1 5.5h11" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round"/>
        </svg>
      </button>

      {open && (
        <div className="svc-datepicker__panel" role="dialog" aria-label="Date picker">
          <div className="svc-datepicker__header">
            <button type="button" className="svc-datepicker__nav" onClick={prevMonth} aria-label="Previous month">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M7.5 2L4 6l3.5 4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <span className="svc-datepicker__month-label">{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" className="svc-datepicker__nav" onClick={nextMonth} aria-label="Next month">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4.5 2L8 6l-3.5 4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>

          <div className="svc-datepicker__grid">
            {DAYS_SHORT.map(d => (
              <span key={d} className="svc-datepicker__weekday">{d}</span>
            ))}
            {cells.map((day, i) => (
              <button
                key={i}
                type="button"
                className={[
                  'svc-datepicker__day',
                  !day              ? 'svc-datepicker__day--empty'    : '',
                  day && isSelected(day) ? 'svc-datepicker__day--selected' : '',
                  day && isToday(day)    ? 'svc-datepicker__day--today'    : '',
                  day && isDisabled(day) ? 'svc-datepicker__day--disabled' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => day && !isDisabled(day) && selectDay(day)}
                disabled={!day || isDisabled(day)}
                tabIndex={!day ? -1 : 0}
                aria-label={day ? `${day} ${MONTHS[viewMonth]} ${viewYear}` : undefined}
              >
                {day || ''}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Stepper({ label, value, onChange, min = 0, max = 20 }) {
  const { t } = useLanguage()
  return (
    <div className="svc-stepper">
      <span className="svc-stepper__label">{label}</span>
      <div className="svc-stepper__controls">
        <button type="button" className="svc-stepper__btn" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label={t('decrease', { label })}>−</button>
        <span className="svc-stepper__value">{value}</span>
        <button type="button" className="svc-stepper__btn" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label={t('increase', { label })}>+</button>
      </div>
    </div>
  )
}

function AirportField({ label, id, value, onChange, airports, loading }) {
  const { t } = useLanguage()
  const [open,  setOpen]  = useState(false)
  const [query, setQuery] = useState(value?.display || '')
  const results           = useAirportSearch(airports, query)

  useEffect(() => {
    if (value && !query) setQuery(`${value.city} (${value.code})`)
  }, [value])

  const select = (airport) => {
    setQuery(`${airport.city} (${airport.code})`)
    onChange(airport)
    setOpen(false)
  }

  return (
    <div className="svc-field">
      <label className="svc-label" htmlFor={id}>{label}</label>
      <div className="svc-airport-wrap">
        <input
          id={id} type="text" className="svc-input"
          value={query} placeholder={t('airportPlaceholder')}
          autoComplete="off"
          onChange={e => { setQuery(e.target.value); setOpen(true); onChange(null) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
        />
        {open && loading && query.length >= 2 && (
          <ul className="svc-airport-dropdown"><li className="svc-airport-option svc-airport-option--muted">{t('loadingAirports')}</li></ul>
        )}
        {open && !loading && results.length > 0 && (
          <ul className="svc-airport-dropdown">
            {results.map(a => (
              <li key={`${a.code}-${a.name}`}>
                <button type="button" className="svc-airport-option" onMouseDown={() => select(a)}>
                  <span className="svc-airport-option__code">{a.code}</span>
                  <span className="svc-airport-option__name">{a.name}</span>
                  <span className="svc-airport-option__city">{a.city}{a.country ? `, ${a.country}` : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {open && !loading && query.length >= 2 && results.length === 0 && (
          <ul className="svc-airport-dropdown"><li className="svc-airport-option svc-airport-option--muted">{t('airportNoResults', { query })}</li></ul>
        )}
      </div>
    </div>
  )
}

function PassportUpload({ label, isMobile, onUploaded }) {
  const { t } = useLanguage()
  const [file,       setFile]       = useState(null)
  const [uploading,  setUploading]  = useState(false)
  const [uploadDone, setUploadDone] = useState(false)
  const [error,      setError]      = useState('')
  const cameraRef = useRef(null)
  const fileRef   = useRef(null)

  const handleFile = useCallback(async (f) => {
    if (!f) return
    if (f.size > 10 * 1024 * 1024) { setError(t('errFileTooLarge')); return }
    setError('')
    setFile(f)
    setUploading(true)
    setUploadDone(false)
    try {
      const data = await uploadPassportDocument(f)
      onUploaded(data.path)
      setUploadDone(true)
    } catch (err) {
      setError(t('errUploadFailed'))
      setFile(null)
      onUploaded(null)
    } finally {
      setUploading(false)
    }
  }, [t, onUploaded])

  const handleRemove = () => {
    setFile(null); setUploadDone(false); setError(''); onUploaded(null)
    if (cameraRef.current) cameraRef.current.value = ''
    if (fileRef.current)   fileRef.current.value   = ''
  }

  return (
    <div className="svc-passport-item">
      <span className="svc-passport-item__label">{label}</span>
      {file ? (
        <div className="svc-passport-item__file">
          {uploading ? (
            <span className="svc-passport-item__uploading">{t('uploading')}</span>
          ) : uploadDone ? (
            <span className="svc-passport-item__check" aria-hidden="true">✓</span>
          ) : null}
          <span className="svc-passport-item__name">{file.name}</span>
          {!uploading && (
            <button type="button" className="svc-passport-item__remove" onClick={handleRemove} aria-label={t('removeFile')}>×</button>
          )}
        </div>
      ) : (
        <div className="svc-passport-item__actions">
          {isMobile ? (
            <>
              <label className="svc-upload-btn svc-upload-btn--camera">
                <input ref={cameraRef} type="file" accept="image/jpeg,image/png" capture="environment" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><path d="M1 4.5C1 3.9 1.4 3.5 2 3.5H3L4 2H9L10 3.5H11C11.6 3.5 12 3.9 12 4.5V10C12 10.6 11.6 11 11 11H2C1.4 11 1 10.6 1 10V4.5Z" stroke="currentColor" strokeWidth="0.85" strokeLinejoin="round" fill="none"/><circle cx="6.5" cy="7" r="1.8" stroke="currentColor" strokeWidth="0.85" fill="none"/></svg>
                {t('camera')}
              </label>
              <label className="svc-upload-btn">
                <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><rect x="1.5" y="2" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="0.85" fill="none"/><path d="M4 5.5h5M4 7.5h3" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round"/></svg>
                {t('file')}
              </label>
            </>
          ) : (
            <label className="svc-upload-btn">
              <input type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
              {t('uploadDocument')}
            </label>
          )}
        </div>
      )}
      {error && <span className="svc-passport-item__error">{error}</span>}
    </div>
  )
}

function PassengerPassportRow({ index, label, savedCompanions, isMobile, onUploaded }) {
  const { t } = useLanguage()
  const [companionId, setCompanionId] = useState('self')
  const hasCompanions = savedCompanions && savedCompanions.length > 0
  const isOwn     = companionId === 'self'
  const companion = savedCompanions?.find((_, i) => String(i) === companionId)

  return (
    <div className="svc-passenger-row">
      <div className="svc-passenger-row__header">
        <span className="svc-passenger-row__label">{label}</span>
        {hasCompanions && (
          <select className="svc-companion-select" value={companionId} onChange={e => setCompanionId(e.target.value)}>
            <option value="self">{index === 0 ? t('myself') : t('selectCompanion')}</option>
            {savedCompanions.map((c, i) => (
              <option key={i} value={String(i)}>{c.name} ({c.role})</option>
            ))}
            <option value="other">{t('otherPassenger')}</option>
          </select>
        )}
      </div>
      {!isOwn && companionId !== 'other' && companion && (
        <div className="svc-companion-prefill">
          <div className="svc-companion-prefill__row"><span className="svc-companion-prefill__key">{t('name')}</span><span className="svc-companion-prefill__val">{companion.name}</span></div>
          <div className="svc-companion-prefill__row"><span className="svc-companion-prefill__key">{t('passport')}</span><span className="svc-companion-prefill__val">{companion.passport || '—'}</span></div>
          <div className="svc-companion-prefill__row"><span className="svc-companion-prefill__key">{t('nationality')}</span><span className="svc-companion-prefill__val">{companion.nationality || '—'}</span></div>
          <p className="svc-companion-prefill__note">{t('companionDocNote')}</p>
        </div>
      )}
      <PassportUpload label={t('passportDocument')} isMobile={isMobile} onUploaded={onUploaded} />
    </div>
  )
}

function ConfirmRow({ label, value }) {
  return (
    <div className="svc-confirm__row">
      <span className="svc-confirm__key">{label}</span>
      <span className="svc-confirm__val">{value}</span>
    </div>
  )
}

const EMPTY_STATE = {
  contactEmail:   '',
  tripType:       'one-way',
  origin:         null,
  destination:    null,
  departDate:     '',
  returnDate:     '',
  flexDates:      false,
  cabinClass:     'business',
  adults:         1,
  children:       0,
  childAges:      [],
  infants:        0,
  bags:           0,
  bagWeight:      '',
  includeAirport: false,
  specialReqs:    '',
  passengerDocs:  [],
}

export default function PrivateAviation({ onBack, member, savedCompanions = [] }) {
  const { t }     = useLanguage()
  const isMobile  = useIsMobile()
  const { airports, loading: airportsLoading } = useAirports()

  const [submitted,   setSubmitted]   = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [formState,   setFormState]   = useState({
    ...EMPTY_STATE,
    contactEmail: member?.email || '',
  })

  const set = (key) => (val) => {
    setFormState(prev => {
      const next = { ...prev, [key]: val }
      saveProgress(next)
      return next
    })
  }

  const { contactEmail, tripType, origin, destination, departDate, returnDate, flexDates,
          cabinClass, adults, children, childAges, infants, bags, bagWeight,
          includeAirport, specialReqs, passengerDocs } = formState

  const draft        = useDraft('private_aviation', formState, setFormState, EMPTY_STATE)
  const saveProgress = draft.saveProgress

  // After draft restore, if contactEmail ended up empty, fall back to member email
  useEffect(() => {
    if (!formState.contactEmail && member?.email) {
      setFormState(prev => ({ ...prev, contactEmail: member.email }))
    }
  }, [draft.draftLoading])

  const passengerCount = adults + children
  useEffect(() => {
    setFormState(prev => {
      const docs = [...(prev.passengerDocs || [])]
      while (docs.length < passengerCount) docs.push(null)
      const trimmed = docs.slice(0, passengerCount)
      if (trimmed.length === prev.passengerDocs?.length) return prev
      return { ...prev, passengerDocs: trimmed }
    })
  }, [passengerCount])

  const handlePassengerDocUploaded = useCallback((index, path) => {
    setFormState(prev => {
      const docs = [...(prev.passengerDocs || [])]
      docs[index] = path
      const next = { ...prev, passengerDocs: docs }
      saveProgress(next)
      return next
    })
  }, [saveProgress])

  const updateChildren = (n) => {
    setFormState(prev => {
      const ages = [...prev.childAges]
      while (ages.length < n) ages.push('')
      const next = { ...prev, children: n, childAges: ages.slice(0, n) }
      saveProgress(next)
      return next
    })
  }

  const updateChildAge = (i, val) => {
    setFormState(prev => {
      const ages = prev.childAges.map((a, idx) => idx === i ? val : a)
      const next = { ...prev, childAges: ages }
      saveProgress(next)
      return next
    })
  }

  const isUrgent = () => {
    if (!departDate) return false
    return (new Date(departDate) - new Date()) < 24 * 60 * 60 * 1000
  }

  const handleSubmit  = (e) => { e.preventDefault(); setShowConfirm(true) }
  const handleConfirm = async () => {
    setSubmitError('')
    try {
      await draft.submitForm(formState)
      setShowConfirm(false)
      setSubmitted(true)
    } catch (_) {
      setSubmitError(t('failedSubmit'))
    }
  }

  /* ── Draft loading ── */
  if (draft.draftLoading) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted" style={{ opacity: 0.5 }}>
          <div className="svc-submitted__icon">◌</div>
          <p className="svc-submitted__body">{t('loading')}</p>
        </div>
      </div>
    )
  }

  if (draft.draftDecision === 'pending') {
    return (
      <div className="db-page svc-page">
        <div className="svc-header">
          <button className="svc-back" onClick={onBack} aria-label={t('back')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('dashboard')}
          </button>
          <div className="svc-header__title-wrap">
            <p className="svc-header__overline">{t('serviceRequest')}</p>
            <h1 className="svc-header__title">{t('privateAviation')}</h1>
          </div>
        </div>
        <DraftPrompt
          draft={draft.existingDraft}
          serviceName={t('privateAviation')}
          onContinue={draft.chooseContinue}
          onStartNew={draft.chooseNew}
        />
      </div>
    )
  }

  /* ── Success ── */
  if (submitted) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted">
          <div className="svc-submitted__icon" aria-hidden="true">✦</div>
          <h2 className="svc-submitted__heading">{t('requestReceived')}</h2>
          <p className="svc-submitted__body">{t('requestReceivedBody')}</p>
          <p className="svc-submitted__sig">{t('signatureMwuilu')}</p>
          <button className="svc-btn-ghost" onClick={onBack}>{t('returnToDashboard')}</button>
        </div>
      </div>
    )
  }

  /* ── Confirm ── */
  if (showConfirm) {
    const uploadedCount = (passengerDocs || []).filter(Boolean).length
    return (
      <div className="db-page svc-page">
        <div className="svc-confirm">
          <p className="svc-confirm__overline">{t('pleaseReview')}</p>
          <h2 className="svc-confirm__heading">{t('privateAviation')}</h2>
          <div className="svc-confirm__rows">
            <ConfirmRow label={t('contactEmail')}     value={contactEmail || '—'} />
            <ConfirmRow label={t('confirmTripType')}  value={tripType === 'return' ? t('returnTrip') : t('oneWay')} />
            <ConfirmRow label={t('confirmFrom')}       value={origin      ? `${origin.city} (${origin.code})` : '—'} />
            <ConfirmRow label={t('confirmTo')}         value={destination ? `${destination.city} (${destination.code})` : '—'} />
            <ConfirmRow label={t('confirmDeparture')}  value={departDate ? isoToDisplay(departDate) : '—'} />
            {tripType === 'return' && <ConfirmRow label={t('confirmReturn')} value={returnDate ? isoToDisplay(returnDate) : '—'} />}
            <ConfirmRow label={t('confirmFlexible')}   value={flexDates ? t('confirmYes') : t('confirmFlexNo')} />
            <ConfirmRow label={t('confirmCabin')}      value={cabinClass === 'first' ? t('firstClass') : t('businessClass')} />
            <ConfirmRow label={t('confirmPassengers')} value={[
              adults   ? `${adults} ${adults     !== 1 ? t('adultPlural')  : t('adultSingular')}`  : '',
              children ? `${children} ${children !== 1 ? t('childPlural')  : t('childSingular')}` : '',
              infants  ? `${infants} ${infants   !== 1 ? t('infantPlural') : t('infantSingular')}` : '',
            ].filter(Boolean).join(', ')} />
            {bags > 0 && <ConfirmRow label={t('confirmBags')} value={`${bags}${bagWeight ? ` × ${bagWeight} ${t('kgPlaceholder')}` : ''}`} />}
            {includeAirport && <ConfirmRow label={t('confirmAirportServices')} value={t('confirmIncluded')} />}
            <ConfirmRow
              label={t('passengerDocuments')}
              value={uploadedCount > 0
                ? t('confirmPassportUploaded') + (uploadedCount < passengerCount ? ` (${uploadedCount}/${passengerCount})` : '')
                : '—'}
            />
            {specialReqs && <ConfirmRow label={t('specialRequirements')} value={specialReqs} />}
          </div>
          {submitError && <p className="mw-error" style={{ marginBottom: 12 }}>{submitError}</p>}
          <div className="svc-confirm__actions">
            <button type="button" className="svc-btn-ghost"   onClick={() => setShowConfirm(false)}>{t('edit')}</button>
            <button type="button" className="svc-btn-primary" onClick={handleConfirm}>{t('confirmSubmit')}</button>
          </div>
        </div>
      </div>
    )
  }

  /* ── Form ── */
  return (
    <div className="db-page svc-page">
      <div className="svc-header">
        <button className="svc-back" onClick={onBack} aria-label={t('back')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {t('dashboard')}
        </button>
        <div className="svc-header__title-wrap">
          <p className="svc-header__overline">{t('serviceRequest')}</p>
          <h1 className="svc-header__title">{t('privateAviation')}</h1>
        </div>
        {isUrgent() && (
          <div className="svc-urgent">
            {t('urgentDeparture')}
            <a href="tel:+32471xxxxxx">{t('urgentDepartureLink')}</a>
          </div>
        )}
      </div>

      <form className="svc-form" onSubmit={handleSubmit} noValidate>

        {/* ── Contact email ── */}
        <div className="svc-section">
          <p className="svc-section__label">{t('contactDetails')}</p>
          <div className="svc-field">
            <label className="svc-label" htmlFor="contactEmail">{t('contactEmail')}</label>
            <input
              id="contactEmail"
              type="email"
              className="svc-input"
              value={contactEmail}
              onChange={e => set('contactEmail')(e.target.value)}
              placeholder="email@example.com"
              autoComplete="email"
            />
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('tripType')}</p>
          <div className="svc-toggle-group">
            <button type="button" className={`svc-toggle${tripType === 'one-way' ? ' svc-toggle--active' : ''}`} onClick={() => set('tripType')('one-way')}>{t('oneWay')}</button>
            <button type="button" className={`svc-toggle${tripType === 'return'  ? ' svc-toggle--active' : ''}`} onClick={() => set('tripType')('return')}>{t('returnTrip')}</button>
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('route')}</p>
          <div className="svc-row-2">
            <AirportField label={t('origin')}      id="origin"      value={origin}      onChange={set('origin')}      airports={airports} loading={airportsLoading} />
            <AirportField label={t('destination')} id="destination" value={destination} onChange={set('destination')} airports={airports} loading={airportsLoading} />
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('dates')}</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="departDate">{t('departureDate')}</label>
              <DatePicker
                id="departDate"
                value={departDate}
                onChange={set('departDate')}
              />
            </div>
            {tripType === 'return' && (
              <div className="svc-field">
                <label className="svc-label" htmlFor="returnDate">{t('returnDate')}</label>
                <DatePicker
                  id="returnDate"
                  value={returnDate}
                  onChange={set('returnDate')}
                  minIso={departDate || undefined}
                />
              </div>
            )}
          </div>
          <label className="svc-checkbox-label">
            <input type="checkbox" className="svc-checkbox" checked={flexDates} onChange={e => set('flexDates')(e.target.checked)} />
            <span>{t('flexibleDates')}</span>
          </label>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('cabinClass')}</p>
          <div className="svc-toggle-group">
            <button type="button" className={`svc-toggle${cabinClass === 'business' ? ' svc-toggle--active' : ''}`} onClick={() => set('cabinClass')('business')}>{t('businessClass')}</button>
            <button type="button" className={`svc-toggle${cabinClass === 'first'    ? ' svc-toggle--active' : ''}`} onClick={() => set('cabinClass')('first')}>{t('firstClass')}</button>
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('passengers')}</p>
          <div className="svc-steppers">
            <Stepper label={t('adults')}   value={adults}   onChange={set('adults')}  min={1} />
            <Stepper label={t('children')} value={children} onChange={updateChildren}         />
            <Stepper label={t('infants')}  value={infants}  onChange={set('infants')}         />
          </div>
          {children > 0 && (
            <div className="svc-child-ages">
              {childAges.map((age, i) => (
                <div key={i} className="svc-field svc-field--inline">
                  <label className="svc-label" htmlFor={`childAge${i}`}>{t('childAge', { n: i + 1 })}</label>
                  <input id={`childAge${i}`} type="number" min="1" max="17" className="svc-input svc-input--sm" value={age} onChange={e => updateChildAge(i, e.target.value)} placeholder={t('agePlaceholder')} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('checkedLuggage')}</p>
          <div className="svc-row-2 svc-row-2--align-end">
            <Stepper label={t('numberOfBags')} value={bags} onChange={set('bags')} />
            {bags > 0 && (
              <div className="svc-field">
                <label className="svc-label" htmlFor="bagWeight">{t('weightPerBag')}</label>
                <input id="bagWeight" type="number" min="1" max="50" className="svc-input svc-input--sm" value={bagWeight} onChange={e => set('bagWeight')(e.target.value)} placeholder={t('kgPlaceholder')} />
              </div>
            )}
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('passengerDocuments')}</p>
          {savedCompanions.length > 0 && (
            <p className="svc-passport-notice" style={{ marginBottom: '16px' }}>
              {t('passportNoticeCompanion')}
            </p>
          )}
          <div className="svc-passport-list">
            {Array.from({ length: passengerCount }).map((_, i) => (
              <PassengerPassportRow
                key={i}
                index={i}
                label={t('passengerN', { n: i + 1 })}
                savedCompanions={i === 0 ? [] : savedCompanions}
                isMobile={isMobile}
                onUploaded={(path) => handlePassengerDocUploaded(i, path)}
              />
            ))}
          </div>
          <p className="svc-passport-notice">{t('passportSecure')}</p>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('additionalServices')}</p>
          <label className="svc-checkbox-label">
            <input type="checkbox" className="svc-checkbox" checked={includeAirport} onChange={e => set('includeAirport')(e.target.checked)} />
            <span>{t('includeMeetGreet')}</span>
          </label>
        </div>

        <div className="svc-section">
          <div className="svc-field">
            <label className="svc-label" htmlFor="specialReqs">{t('specialRequirements')}</label>
            <textarea id="specialReqs" className="svc-textarea" rows={3} value={specialReqs} onChange={e => set('specialReqs')(e.target.value)} placeholder={t('specialReqsPlaceholder')} />
          </div>
        </div>

        <div className="svc-form__footer">
          <button type="submit" className="svc-btn-primary svc-btn-primary--wide">{t('reviewRequest')}</button>
        </div>

      </form>
    </div>
  )
}